import React, { useState } from 'react';
import { Calculator, Home } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const MiniSimulateurRendementLocatif = () => {
  const [formData, setFormData] = useState({
    prixAchat: 450000,
    loyerMensuel: 1650,
    tauxHypotheque: 2.0,
    fondsPropresPct: 25,
  });

  const [results, setResults] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
    setResults(null);
    setShowEmailForm(false);
  };

  const calculateRendement = () => {
    const { prixAchat, loyerMensuel, tauxHypotheque, fondsPropresPct } = formData;

    // Calculs de base
    const loyerAnnuel = loyerMensuel * 12;
    const fondsPropres = prixAchat * (fondsPropresPct / 100);
    const hypotheque = prixAchat - fondsPropres;

    // Rendement brut
    const rendementBrut = (loyerAnnuel / prixAchat) * 100;

    // Charges annuelles
    const interetsHypotheque = hypotheque * (tauxHypotheque / 100);
    const entretien = prixAchat * 0.01; // 1% de la valeur
    const assurancesEtFrais = 1500; // Forfait annuel
    const chargesTheoriques = hypotheque * 0.05; // Calcul théorique banques à 5%

    const chargesReelles = interetsHypotheque + entretien + assurancesEtFrais;

    // Cash-flow
    const cashFlowAnnuel = loyerAnnuel - chargesReelles;
    const cashFlowMensuel = cashFlowAnnuel / 12;

    // Rendement net
    const rendementNet = ((loyerAnnuel - chargesReelles) / prixAchat) * 100;

    // Cash-on-Cash Return
    const cashOnCash = (cashFlowAnnuel / fondsPropres) * 100;

    // Test de soutenabilité bancaire
    const soutenabiliteBancaire = loyerAnnuel >= (chargesTheoriques + entretien);

    // Revenu locatif net imposable (avec amortissement 1%)
    const amortissement = prixAchat * 0.01;
    const revenuImposable = Math.max(0, loyerAnnuel - chargesReelles - amortissement);

    setResults({
      loyerAnnuel: Math.round(loyerAnnuel),
      fondsPropres: Math.round(fondsPropres),
      hypotheque: Math.round(hypotheque),
      rendementBrut: rendementBrut.toFixed(2),
      rendementNet: rendementNet.toFixed(2),
      cashOnCash: cashOnCash.toFixed(2),
      cashFlowAnnuel: Math.round(cashFlowAnnuel),
      cashFlowMensuel: Math.round(cashFlowMensuel),
      interetsHypotheque: Math.round(interetsHypotheque),
      chargesReelles: Math.round(chargesReelles),
      soutenabiliteBancaire,
      revenuImposable: Math.round(revenuImposable)
    });

    // Afficher le formulaire email après le calcul
    setShowEmailForm(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !results) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await axios.post(`${API_BASE_URL}/lead-magnet`, {
        email: email,
        source: 'guide_investissement_locatif',
        guide: 'investissement_locatif',
        simulation_data: {
          prixAchat: formData.prixAchat,
          loyerMensuel: formData.loyerMensuel,
          tauxHypotheque: formData.tauxHypotheque,
          fondsPropresPct: formData.fondsPropresPct,
          rendementBrut: results.rendementBrut,
          rendementNet: results.rendementNet,
          cashOnCash: results.cashOnCash,
          cashFlowAnnuel: results.cashFlowAnnuel
        }
      });

      if (response.data.success) {
        setSubmitMessage('✅ Parfait ! Consultez votre boîte mail pour recevoir votre rapport personnalisé.');
        setEmail('');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitMessage('❌ Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8" id="simulateur">>
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="w-8 h-8 text-yellow-400" />
        <h2 className="text-3xl font-bold text-white">Simulateur de Rendement Locatif</h2>
      </div>

      <p className="text-gray-300 mb-6">
        Calculez le rendement, le cash-flow et la soutenabilité de votre investissement locatif.
      </p>

      {/* Formulaire */}
      <div className="space-y-6 mb-6">
        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Prix d'achat du bien (CHF)
          </label>
          <input
            type="range"
            name="prixAchat"
            min="200000"
            max="2000000"
            step="50000"
            value={formData.prixAchat}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>CHF 200'000</span>
            <span className="text-yellow-400 font-bold">CHF {formData.prixAchat.toLocaleString()}</span>
            <span>CHF 2'000'000</span>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Loyer mensuel (CHF)
          </label>
          <input
            type="range"
            name="loyerMensuel"
            min="500"
            max="10000"
            step="50"
            value={formData.loyerMensuel}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>CHF 500</span>
            <span className="text-yellow-400 font-bold">CHF {formData.loyerMensuel.toLocaleString()}</span>
            <span>CHF 10'000</span>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Taux hypothécaire (%)
          </label>
          <input
            type="range"
            name="tauxHypotheque"
            min="1.0"
            max="5.0"
            step="0.1"
            value={formData.tauxHypotheque}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>1.0%</span>
            <span className="text-yellow-400 font-bold">{formData.tauxHypotheque}%</span>
            <span>5.0%</span>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Fonds propres (% du prix)
          </label>
          <input
            type="range"
            name="fondsPropresPct"
            min="20"
            max="50"
            step="5"
            value={formData.fondsPropresPct}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>20%</span>
            <span className="text-yellow-400 font-bold">{formData.fondsPropresPct}%</span>
            <span>50%</span>
          </div>
        </div>

        <button
          onClick={calculateRendement}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calculer le rendement
        </button>
      </div>

      {/* Résultats */}
      {results && (
        <div className="space-y-4 mb-6 p-6 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/30">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Home className="w-6 h-6 text-yellow-400" />
            Résultats de votre simulation
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-black/30 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Rendement brut</p>
              <p className="text-2xl font-bold text-yellow-400">{results.rendementBrut}%</p>
            </div>

            <div className="p-4 bg-black/30 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Rendement net</p>
              <p className="text-2xl font-bold text-green-400">{results.rendementNet}%</p>
            </div>

            <div className="p-4 bg-black/30 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Cash-on-Cash Return</p>
              <p className={`text-2xl font-bold ${parseFloat(results.cashOnCash) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {results.cashOnCash}%
              </p>
            </div>

            <div className="p-4 bg-black/30 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Cash-flow mensuel</p>
              <p className={`text-2xl font-bold ${results.cashFlowMensuel >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                CHF {results.cashFlowMensuel.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3 mt-4">
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-gray-400 text-xs mb-1">Fonds propres</p>
              <p className="text-white font-semibold">CHF {results.fondsPropres.toLocaleString()}</p>
            </div>

            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-gray-400 text-xs mb-1">Hypothèque</p>
              <p className="text-white font-semibold">CHF {results.hypotheque.toLocaleString()}</p>
            </div>

            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-gray-400 text-xs mb-1">Loyers annuels</p>
              <p className="text-white font-semibold">CHF {results.loyerAnnuel.toLocaleString()}</p>
            </div>
          </div>

          <div className={`p-4 rounded-lg border-l-4 mt-4 ${
            results.soutenabiliteBancaire 
              ? 'bg-green-500/10 border-green-500' 
              : 'bg-red-500/10 border-red-500'
          }`}>
            <p className="text-white font-semibold mb-2">
              {results.soutenabiliteBancaire ? '✅' : '❌'} Soutenabilité bancaire
            </p>
            <p className="text-gray-300 text-sm">
              {results.soutenabiliteBancaire 
                ? 'Les revenus locatifs couvrent les charges théoriques. Ce bien peut être financé.'
                : 'Les revenus locatifs ne couvrent pas les charges théoriques (5%). Vous devrez démontrer des revenus personnels suffisants.'}
            </p>
          </div>

          <div className="p-4 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
            <p className="text-white font-semibold mb-2">💡 Fiscalité</p>
            <p className="text-gray-300 text-sm">
              Revenu locatif net imposable (après déductions et amortissement) : <strong className="text-white">CHF {results.revenuImposable.toLocaleString()}</strong>
            </p>
            <p className="text-gray-400 text-xs mt-1">
              (Au lieu de CHF {results.loyerAnnuel.toLocaleString()} de loyers bruts)
            </p>
          </div>
        </div>
      )}

      {/* Formulaire Email */}
      {showEmailForm && results && (
        <div className="p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl border border-yellow-500/30">
          <h3 className="text-xl font-bold text-white mb-3">
            📧 Recevez votre rapport complet par email
          </h3>
          <p className="text-gray-300 text-sm mb-4">
            Obtenez votre simulation personnalisée + votre rapport complet avec toutes les stratégies d'optimisation.
          </p>

          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon rapport par email'}
            </button>
          </form>

          {submitMessage && (
            <p className={`mt-3 text-sm ${submitMessage.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>
              {submitMessage}
            </p>
          )}

          <p className="text-gray-400 text-xs mt-3 text-center">
            🔒 Vos données sont sécurisées et ne seront jamais partagées
          </p>
        </div>
      )}
    </div>
  );
};

export default MiniSimulateurRendementLocatif;
